/**
 * Chat reducer and pure functions for chat state management.
 * Extracted from useChatSession for testability.
 */

import type { ClarifyQuestion } from "@prelude-ai/prelude-agentic-framework"
import type { SSEEvent } from "../../types/index.ts"
import type { ChatMessage, StepInfo } from "../types.ts"
import {
  createMessage as createMessageUtil,
  shouldSaveIntermediateResponse,
  updateStepHistory as updateStepHistoryUtil,
  updateStepPath as updateStepPathUtil
} from "../utils/chat-state.ts"

// ============================================================================
// Types
// ============================================================================

export type ToolCallInfo = {
  toolName: string
  toolArguments?: Record<string, unknown>
  toolCallId?: string
}

export type LastTurnInfo = {
  symbol: string | null
  toolInfo: ToolCallInfo | null
}

export type PendingClarification = {
  questions: ClarifyQuestion[]
  toolCallId: string
}

export type ChatState = {
  messages: ChatMessage[]
  currentResponse: string
  isLoading: boolean
  clarification: PendingClarification | null
  currentThought: string | null
  stepHistory: StepInfo[]
  stepPath: string[]
  flowState: Record<string, unknown>
  currentToolCall: ToolCallInfo | null
  lastAddedMessage: string
  lastTurnInfo: LastTurnInfo
}

export type ChatAction =
  | { type: "SEND_START"; message: ChatMessage }
  | { type: "CLEAR_CLARIFICATION" }
  | { type: "RESTORE_CLARIFICATION"; clarification: PendingClarification }
  | { type: "SSE_TURN"; event: SSEEvent }
  | { type: "SSE_DONE"; event: SSEEvent }
  | { type: "SSE_CLARIFY"; event: SSEEvent }
  | { type: "SSE_PAUSED"; event: SSEEvent }
  | { type: "SSE_ERROR"; event: SSEEvent }
  | { type: "SSE_RESULT"; event: SSEEvent }
  | { type: "SSE_THOUGHT"; event: SSEEvent }
  | { type: "STREAM_ERROR"; error: string }
  | { type: "STREAM_END" }

export const initialState: ChatState = {
  messages: [],
  currentResponse: "",
  isLoading: false,
  clarification: null,
  currentThought: null,
  stepHistory: [],
  stepPath: [],
  flowState: {},
  currentToolCall: null,
  lastAddedMessage: "",
  lastTurnInfo: { symbol: null, toolInfo: null }
}

// ============================================================================
// Helpers
// ============================================================================

export function createMessage(
  content: string,
  role: "user" | "assistant" | "system",
  extras?: Partial<ChatMessage>
): ChatMessage {
  const base = createMessageUtil(role, content)
  return extras ? { ...base, ...extras } : base
}

/**
 * Maybe save the previous response as an intermediate message.
 * Used when a new turn arrives to preserve the previous turn's content.
 */
export function maybeSavePreviousResponse(state: ChatState): {
  messages: ChatMessage[]
  lastAddedMessage: string
} {
  const { currentResponse, lastAddedMessage, lastTurnInfo } = state

  if (!shouldSaveIntermediateResponse(currentResponse, "", lastAddedMessage)) {
    return { messages: state.messages, lastAddedMessage }
  }

  const message = createMessage(currentResponse, "assistant", {
    toolName: lastTurnInfo.toolInfo?.toolName,
    toolArguments: lastTurnInfo.toolInfo?.toolArguments,
    toolCallId: lastTurnInfo.toolInfo?.toolCallId,
    isToolCall: Boolean(lastTurnInfo.toolInfo?.toolName),
    symbol: lastTurnInfo.symbol ?? undefined
  })

  return {
    messages: [...state.messages, message],
    lastAddedMessage: currentResponse
  }
}

/**
 * Finalize the current response as a message.
 * Used by done/clarify/paused events to save the final response.
 */
export function finalizeCurrentResponse(
  state: ChatState,
  fallbackContent?: string
): {
  messages: ChatMessage[]
  lastAddedMessage: string
} {
  const content = state.currentResponse || fallbackContent || ""

  if (!content || content === state.lastAddedMessage) {
    return { messages: state.messages, lastAddedMessage: state.lastAddedMessage }
  }

  // Dedupe: check if last message already has this content
  const lastMessage = state.messages[state.messages.length - 1]
  if (lastMessage?.role === "assistant" && lastMessage.content === content) {
    return { messages: state.messages, lastAddedMessage: content }
  }

  const message = createMessage(content, "assistant")
  return {
    messages: [...state.messages, message],
    lastAddedMessage: content
  }
}

// ============================================================================
// Event Handlers
// ============================================================================

export function handleTurnEvent(state: ChatState, event: SSEEvent): ChatState {
  if (!event.response) return state

  const { messages, lastAddedMessage } = maybeSavePreviousResponse(state)

  const newToolInfo: ToolCallInfo | null = event.toolName
    ? {
        toolName: event.toolName,
        toolArguments: event.toolArguments,
        toolCallId: event.toolCallId
      }
    : null

  // Only user-facing turns stream into the visible response: the agent's answer (`respond`)
  // and untagged turns. Internal reasoning moves — reflect, pause, continue, abandon — are not
  // rendered; they still feed the trace via stepHistory below. Tool calls keep streaming so their
  // "Calling tools" entries still render in dev mode.
  const isUserFacingTurn = !event.symbol || event.symbol === "respond" || Boolean(event.toolName)

  return {
    ...state,
    messages,
    currentResponse: isUserFacingTurn ? event.response : state.currentResponse,
    lastAddedMessage,
    lastTurnInfo: {
      symbol: event.symbol ?? null,
      toolInfo: newToolInfo
    },
    currentToolCall: newToolInfo,
    stepPath: updateStepPathUtil(state.stepPath, event),
    stepHistory: updateStepHistoryUtil(state.stepHistory, event),
    flowState: event.flowState && typeof event.flowState === "object" ? event.flowState : state.flowState
  }
}

export function handleDoneEvent(state: ChatState, event: SSEEvent): ChatState {
  const { messages, lastAddedMessage } = finalizeCurrentResponse(state, event.response)

  return {
    ...state,
    messages,
    currentResponse: "",
    currentThought: null,
    lastAddedMessage,
    isLoading: false,
    currentToolCall: null
  }
}

export function handleClarifyOrPausedEvent(state: ChatState, event: SSEEvent): ChatState {
  const { messages, lastAddedMessage } = finalizeCurrentResponse(state)

  const clarification: PendingClarification | null =
    event.questions && event.toolCallId ? { questions: event.questions, toolCallId: event.toolCallId } : null

  return {
    ...state,
    messages,
    currentResponse: "",
    lastAddedMessage,
    clarification,
    isLoading: false,
    currentThought: null
  }
}

export function handleErrorEvent(state: ChatState, event: SSEEvent): ChatState {
  const errorMessage = createMessage(event.message || event.error || "Unknown error", "system")

  return {
    ...state,
    messages: [...state.messages, errorMessage],
    isLoading: false,
    currentThought: null,
    currentToolCall: null
  }
}

// ============================================================================
// Reducer
// ============================================================================

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SEND_START":
      return {
        ...state,
        messages: [...state.messages, action.message],
        isLoading: true,
        currentResponse: "",
        currentThought: null,
        lastAddedMessage: "",
        lastTurnInfo: { symbol: null, toolInfo: null }
      }

    case "CLEAR_CLARIFICATION":
      return {
        ...state,
        clarification: null
      }

    case "RESTORE_CLARIFICATION":
      return {
        ...state,
        clarification: action.clarification
      }

    case "SSE_TURN":
      return handleTurnEvent(state, action.event)

    case "SSE_DONE":
      return handleDoneEvent(state, action.event)

    case "SSE_CLARIFY":
    case "SSE_PAUSED":
      return handleClarifyOrPausedEvent(state, action.event)

    case "SSE_ERROR":
      return handleErrorEvent(state, action.event)

    case "SSE_THOUGHT":
      return {
        ...state,
        currentThought: action.event.response ?? null
      }

    case "SSE_RESULT":
      // Background agent result - treat like done
      return handleDoneEvent(state, action.event)

    case "STREAM_ERROR": {
      const errorMessage = createMessage(`Error: ${action.error}`, "system")
      return {
        ...state,
        messages: [...state.messages, errorMessage],
        isLoading: false,
        currentResponse: ""
      }
    }

    case "STREAM_END":
      return {
        ...state,
        isLoading: false,
        currentResponse: ""
      }

    default:
      return state
  }
}
